import {
  Activity,
  AlertTriangle,
  ArrowDownWideNarrow,
  ArrowUpWideNarrow,
  Boxes,
  CheckCircle2,
  CirclePause,
  Cloud,
  Copy,
  DatabaseBackup,
  FileText,
  Gauge,
  HardDrive,
  History,
  Info,
  Loader2,
  Moon,
  Play,
  Power,
  RefreshCcw,
  RotateCcw,
  Server,
  Settings,
  ShieldCheck,
  Sun,
  Terminal,
  Users,
  Wifi,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  CommandType,
  DashboardCommand,
  LogEntry,
  MinecraftSnapshot,
  ServerStatus,
} from "@minecraft-dashboard/shared";
import { COMMAND_TYPES, humanBytes, snapshotIsStale } from "@minecraft-dashboard/shared";
import { fetchCommands, fetchStatus, postCommand } from "./api.js";
import { getDashboardConfig } from "./types.js";

type Theme = "light" | "dark";
type TabId = "overview" | "logs" | "backups" | "settings";
type LogFilter = "all" | LogEntry["level"];
type LogSortOrder = "desc" | "asc";
type Toast = { id: string; tone: "success" | "error" | "info"; message: string };

const STATUS_LABELS: Record<ServerStatus, string> = {
  running: "稼働中",
  idle: "待機中",
  starting: "起動中",
  stopped: "停止中",
  error: "エラー",
  unknown: "確認中",
};

const STATUS_ICONS: Record<ServerStatus, typeof CheckCircle2> = {
  running: CheckCircle2,
  idle: CirclePause,
  starting: Loader2,
  stopped: Power,
  error: AlertTriangle,
  unknown: Info,
};

const ACTION_META: Record<CommandType, { label: string; icon: typeof Play; danger?: boolean }> = {
  start: { label: "起動", icon: Play },
  stop: { label: "停止", icon: Power, danger: true },
  restart: { label: "再起動", icon: RotateCcw, danger: true },
  backup: { label: "バックアップ", icon: DatabaseBackup },
  status: { label: "更新", icon: RefreshCcw },
};

const COMMAND_STATUS_LABELS: Record<DashboardCommand["status"], string> = {
  queued: "待機中",
  running: "実行中",
  succeeded: "成功",
  failed: "失敗",
  expired: "期限切れ",
  rejected: "拒否",
};

const LOG_FILTER_LABELS: Record<LogFilter, string> = {
  all: "すべて",
  info: "情報",
  error: "エラー",
  warn: "警告",
  join: "参加",
  leave: "退出",
  ready: "起動",
  pause: "一時停止",
};

const STATUS_REASON_LABELS: Record<string, string> = {
  "Collector is starting": "状態収集を開始しています",
  "Recent server log contains a fatal error": "直近のサーバーログに致命的なエラーがあります",
  "launchd appears to be restarting repeatedly": "launchdサービスが繰り返し再起動している可能性があります",
  "Server is paused while empty; the port remains available": "プレイヤー不在のためサーバーは一時停止中です。ポートは待受中です",
  "launchd is running, TCP is listening, and Minecraft status ping succeeded": "launchd、TCP待受、Minecraftステータス応答がすべて正常です",
  "launchd is running while Minecraft status ping is not ready": "launchdは稼働中ですが、Minecraftのステータス応答はまだ準備中です",
  "launchd is stopped and the Minecraft port is not listening": "launchdは停止中で、Minecraftポートも待受していません",
  "Minecraft port is listening without the expected launchd service": "Minecraftポートは待受中ですが、想定したlaunchdサービスが稼働していません",
  "Collector does not have enough data yet": "状態判定に必要なデータを収集中です",
};

const UNKNOWN_TEXT = "不明";

const API_ERROR_LABELS: Record<string, string> = {
  "Unable to refresh": "更新できませんでした",
  "Command failed": "操作に失敗しました",
  "Command not found": "操作が見つかりません",
  "Empty response": "空の応答を受信しました",
  "Unsupported command type": "対応していない操作です",
  "Internal error": "内部エラーが発生しました",
  "Not found": "見つかりません",
  "Cloudflare Access token is required": "Cloudflare Accessの認証が必要です",
  "Cloudflare Access identity is required": "Cloudflare Accessのログイン情報が必要です",
  "Access denied": "アクセスが拒否されました",
};

export function DashboardApp() {
  const config = useMemo(() => getDashboardConfig(), []);
  const [snapshot, setSnapshot] = useState<MinecraftSnapshot | null>(null);
  const [commands, setCommands] = useState<DashboardCommand[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyAction, setBusyAction] = useState<CommandType | null>(null);
  const [confirmAction, setConfirmAction] = useState<CommandType | null>(null);
  const [tab, setTab] = useState<TabId>("overview");
  const [logFilter, setLogFilter] = useState<LogFilter>("all");
  const [logSortOrder, setLogSortOrder] = useState<LogSortOrder>("desc");
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [theme, setTheme] = useState<Theme>(() => preferredTheme());
  const abortRef = useRef<AbortController | null>(null);

  const staleAfterSeconds = 15;
  const stale = snapshotIsStale(snapshot, staleAfterSeconds);
  const refreshIntervalMs = config.mode === "local" ? 2500 : 15000;
  const storageWarning = snapshot ? storageWarningMessage(snapshot) : null;

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem("minecraft-dashboard-theme", theme);
  }, [theme]);

  const pushToast = useCallback((toast: Omit<Toast, "id">) => {
    const id = crypto.randomUUID();
    setToasts((current) => [...current, { ...toast, id }].slice(-4));
    window.setTimeout(() => {
      setToasts((current) => current.filter((item) => item.id !== id));
    }, 4200);
  }, []);

  const refresh = useCallback(
    async (showErrors = false) => {
      abortRef.current?.abort();
      const abort = new AbortController();
      abortRef.current = abort;
      try {
        const [statusResult, commandsResult] = await Promise.all([
          fetchStatus(config.apiBase, abort.signal),
          fetchCommands(config.apiBase, abort.signal).catch(() => ({ ok: true, commands: [] })),
        ]);
        setSnapshot(statusResult.snapshot);
        setCommands(commandsResult.commands);
        setError(statusResult.error ? localizedErrorMessage(statusResult.error) : null);
      } catch (caught) {
        if (abort.signal.aborted) return;
        const message = localizedErrorMessage(caught instanceof Error ? caught.message : "Unable to refresh");
        setError(message);
        if (showErrors) pushToast({ tone: "error", message });
      } finally {
        if (!abort.signal.aborted) setLoading(false);
      }
    },
    [config.apiBase, pushToast],
  );

  useEffect(() => {
    void refresh(false);
    const timer = window.setInterval(() => void refresh(false), refreshIntervalMs);
    return () => {
      window.clearInterval(timer);
      abortRef.current?.abort();
    };
  }, [refresh, refreshIntervalMs]);

  const runCommand = useCallback(
    async (type: CommandType) => {
      setBusyAction(type);
      try {
        const result = await postCommand(config.apiBase, { type });
        if (!result.ok || !result.command) throw new Error(result.error ?? "Command failed");
        pushToast({
          tone: result.command.result?.ok === false ? "error" : "success",
          message:
            config.mode === "local"
              ? `${ACTION_META[type].label}が完了しました`
              : `${ACTION_META[type].label}をCloudflare側に送信しました`,
        });
        await refresh(false);
      } catch (caught) {
        const message = localizedErrorMessage(caught instanceof Error ? caught.message : `${ACTION_META[type].label}に失敗しました`);
        pushToast({ tone: "error", message });
      } finally {
        setBusyAction(null);
        setConfirmAction(null);
      }
    },
    [config.apiBase, config.mode, pushToast, refresh],
  );

  const onAction = (type: CommandType) => {
    if (ACTION_META[type].danger) {
      setConfirmAction(type);
      return;
    }
    void runCommand(type);
  };

  const copyAddress = async () => {
    if (!snapshot?.serverAddress) return;
    await navigator.clipboard.writeText(snapshot.serverAddress);
    pushToast({ tone: "success", message: "サーバーアドレスをコピーしました" });
  };

  const filteredLogs = useMemo(() => {
    const logs = snapshot?.logs ?? [];
    return logs
      .map((entry, index) => ({ entry, index }))
      .filter(({ entry }) => logFilter === "all" || entry.level === logFilter)
      .sort((left, right) => (logSortOrder === "desc" ? right.index - left.index : left.index - right.index))
      .map(({ entry }) => entry);
  }, [logFilter, logSortOrder, snapshot]);

  return (
    <div className="app-shell">
      <header className="topbar">
        <div className="brand-lockup">
          <div className="brand-mark" aria-hidden="true">
            <Boxes size={24} />
          </div>
          <div>
            <p className="eyebrow">{config.mode === "local" ? "Mac mini ローカル" : "Cloudflare 公開"}</p>
            <h1>{config.title}</h1>
          </div>
        </div>
        <div className="topbar-actions">
          <button className="icon-button" type="button" onClick={() => void refresh(true)} title="更新" aria-label="更新">
            <RefreshCcw size={18} />
          </button>
          <button
            className="icon-button theme-button"
            type="button"
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            title={theme === "dark" ? "ライトテーマに切り替え" : "ダークテーマに切り替え"}
            aria-label={theme === "dark" ? "ライトテーマに切り替え" : "ダークテーマに切り替え"}
            aria-pressed={theme === "dark"}
          >
            {theme === "dark" ? <Moon size={18} /> : <Sun size={18} />}
          </button>
        </div>
      </header>

      {error && (
        <div className="banner banner-error" role="alert">
          <AlertTriangle size={18} />
          <span>{error}</span>
        </div>
      )}

      {stale && snapshot && (
        <div className="banner banner-warn" role="status">
          <AlertTriangle size={18} />
          <span>状態データが古くなっています。最終更新は{formatRelative(snapshot.collectedAt)}です。</span>
        </div>
      )}

      {storageWarning && (
        <div className="banner banner-warn" role="status">
          <HardDrive size={18} />
          <span>{storageWarning}</span>
        </div>
      )}

      {loading ? (
        <LoadingView />
      ) : snapshot ? (
        <>
          <StatusHero snapshot={snapshot} stale={stale} />
          <ActionBar
            busyAction={busyAction}
            disabled={Boolean(busyAction)}
            onAction={onAction}
            onCopyAddress={() => void copyAddress()}
            onOpenLogs={() => setTab("logs")}
          />
          <nav className="tabs" aria-label="ダッシュボードの切り替え">
            <TabButton active={tab === "overview"} icon={Gauge} label="概要" onClick={() => setTab("overview")} />
            <TabButton active={tab === "logs"} icon={FileText} label="ログ" onClick={() => setTab("logs")} />
            <TabButton active={tab === "backups"} icon={History} label="バックアップ" onClick={() => setTab("backups")} />
            <TabButton active={tab === "settings"} icon={Settings} label="設定" onClick={() => setTab("settings")} />
          </nav>

          {tab === "overview" && <Overview snapshot={snapshot} commands={commands} />}
          {tab === "logs" && (
            <LogsView
              logs={filteredLogs}
              activeFilter={logFilter}
              sortOrder={logSortOrder}
              onFilterChange={setLogFilter}
              onSortOrderChange={setLogSortOrder}
            />
          )}
          {tab === "backups" && <BackupsView snapshot={snapshot} />}
          {tab === "settings" && <SettingsView snapshot={snapshot} mode={config.mode} />}
        </>
      ) : (
        <EmptyView />
      )}

      <ToastStack toasts={toasts} onDismiss={(id) => setToasts((items) => items.filter((item) => item.id !== id))} />

      {confirmAction && (
        <ConfirmModal
          action={confirmAction}
          busy={busyAction === confirmAction}
          onCancel={() => setConfirmAction(null)}
          onConfirm={() => void runCommand(confirmAction)}
        />
      )}
    </div>
  );
}

function StatusHero({ snapshot, stale }: { snapshot: MinecraftSnapshot; stale: boolean }) {
  const Icon = STATUS_ICONS[snapshot.status];
  return (
    <section className={`status-hero status-${snapshot.status}`}>
      <div className="status-main">
        <div className="status-orb" aria-hidden="true">
          <Icon size={34} className={snapshot.status === "starting" ? "spin" : ""} />
        </div>
        <div>
          <p className="eyebrow">サーバー状態</p>
          <h2>{STATUS_LABELS[snapshot.status]}</h2>
          <p className="status-reason">{statusReasonLabel(snapshot.statusReason)}</p>
        </div>
      </div>
      <div className="hero-metrics">
        <MiniMetric icon={Users} label="プレイヤー" value={playersLabel(snapshot)} />
        <MiniMetric icon={Activity} label="応答速度" value={snapshot.ping.latencyMs == null ? UNKNOWN_TEXT : `${snapshot.ping.latencyMs} ms`} />
        <MiniMetric icon={Wifi} label="接続先" value={snapshot.serverAddress} />
        <MiniMetric icon={Cloud} label="更新" value={stale ? "古い状態" : formatRelative(snapshot.collectedAt)} />
      </div>
    </section>
  );
}

function ActionBar({
  busyAction,
  disabled,
  onAction,
  onCopyAddress,
  onOpenLogs,
}: {
  busyAction: CommandType | null;
  disabled: boolean;
  onAction: (type: CommandType) => void;
  onCopyAddress: () => void;
  onOpenLogs: () => void;
}) {
  return (
    <section className="action-strip" aria-label="サーバー操作">
      {COMMAND_TYPES.map((type) => {
        const meta = ACTION_META[type];
        const Icon = meta.icon;
        const busy = busyAction === type;
        return (
          <button
            key={type}
            className={`action-button ${meta.danger ? "danger" : ""}`}
            type="button"
            disabled={disabled}
            onClick={() => onAction(type)}
            title={meta.label}
          >
            {busy ? <Loader2 className="spin" size={18} /> : <Icon size={18} />}
            <span>{meta.label}</span>
          </button>
        );
      })}
      <button className="action-button secondary" type="button" onClick={onOpenLogs} title="ログを開く">
        <Terminal size={18} />
        <span>ログを開く</span>
      </button>
      <button className="action-button secondary" type="button" onClick={onCopyAddress} title="サーバーアドレスをコピー">
        <Copy size={18} />
        <span>アドレスをコピー</span>
      </button>
    </section>
  );
}

function Overview({ snapshot, commands }: { snapshot: MinecraftSnapshot; commands: DashboardCommand[] }) {
  const latestCommands = commands.slice(0, 5);
  return (
    <main className="content-grid">
      <section className="panel metrics-panel">
        <h3>実行状態</h3>
        <div className="metric-grid">
          <Metric icon={Server} label="launchd" value={launchdStateLabel(snapshot.launchd.rawState, snapshot.launchd.running)} detail={`PID ${snapshot.launchd.pid ?? "なし"}`} />
          <Metric icon={Activity} label="Java CPU使用率" value={snapshot.process.cpuPercent == null ? UNKNOWN_TEXT : `${snapshot.process.cpuPercent.toFixed(1)}%`} detail={snapshot.process.elapsed ?? "経過時間不明"} />
          <Metric icon={Gauge} label="メモリ" value={bytesLabel(snapshot.process.memoryRssBytes)} detail="Java RSS" />
          <Metric icon={HardDrive} label="ストレージ" value={snapshot.disk.serverDirHuman ?? UNKNOWN_TEXT} detail={storageDetail(snapshot)} />
        </div>
      </section>

      <section className="panel">
        <h3>Minecraft</h3>
        <dl className="facts">
          <Fact label="バージョン" value={snapshot.ping.version ?? UNKNOWN_TEXT} />
          <Fact label="サーバー説明" value={snapshot.ping.motd || UNKNOWN_TEXT} />
          <Fact label="ポート" value={snapshot.tcp.listening ? `${snapshot.tcp.host}:${snapshot.tcp.port}` : "待受なし"} />
          <Fact label="Tailscale" value={snapshot.tailscale.ok ? snapshot.tailscale.selfName ?? "オンライン" : snapshot.tailscale.error ?? UNKNOWN_TEXT} />
        </dl>
      </section>

      <section className="panel">
        <h3>エージェント</h3>
        <dl className="facts">
          <Fact label="状態" value={snapshot.agent.ok ? "正常" : "確認が必要"} />
          <Fact label="稼働時間" value={duration(snapshot.agent.uptimeSeconds)} />
          <Fact label="Cloudflare同期" value={snapshot.agent.cloudSyncOk == null ? "未設定" : snapshot.agent.cloudSyncOk ? "正常" : "失敗中"} />
          <Fact label="同期間隔" value={snapshot.agent.cloudSyncIntervalMs == null ? "ローカルのみ" : durationMs(snapshot.agent.cloudSyncIntervalMs)} />
          <Fact label="取得日時" value={new Date(snapshot.collectedAt).toLocaleString("ja-JP")} />
        </dl>
      </section>

      <section className="panel">
        <div className="panel-heading">
          <h3>ストレージ</h3>
          <span className={`status-pill ${snapshot.disk.external ? "pill-external" : ""}`}>
            {snapshot.disk.external ? "外付け" : "内蔵"}
          </span>
        </div>
        <dl className="facts">
          <Fact label="ボリューム" value={snapshot.disk.volumeName ?? snapshot.disk.mountPoint ?? UNKNOWN_TEXT} />
          <Fact label="空き容量" value={snapshot.disk.freeHuman ?? UNKNOWN_TEXT} />
          <Fact label="使用率" value={snapshot.disk.usedPercent == null ? UNKNOWN_TEXT : `${snapshot.disk.usedPercent}%`} />
          <Fact label="書き込み" value={snapshot.disk.writable == null ? UNKNOWN_TEXT : snapshot.disk.writable ? "可能" : "不可"} />
        </dl>
      </section>

      <section className="panel">
        <h3>最近の操作</h3>
        {latestCommands.length ? (
          <div className="command-list">
            {latestCommands.map((command) => (
              <div className="command-row" key={command.id}>
                <span className={`command-dot command-${command.status}`} />
                <span className="command-type">{ACTION_META[command.type].label}</span>
                <span>{COMMAND_STATUS_LABELS[command.status]}</span>
                <time>{formatRelative(command.requestedAt)}</time>
              </div>
            ))}
          </div>
        ) : (
          <p className="muted">操作履歴はまだありません。</p>
        )}
      </section>
    </main>
  );
}

function LogsView({
  logs,
  activeFilter,
  sortOrder,
  onFilterChange,
  onSortOrderChange,
}: {
  logs: LogEntry[];
  activeFilter: LogFilter;
  sortOrder: LogSortOrder;
  onFilterChange: (filter: LogFilter) => void;
  onSortOrderChange: (sortOrder: LogSortOrder) => void;
}) {
  const filters: LogFilter[] = ["all", "error", "warn", "join", "leave", "ready", "pause"];
  const SortIcon = sortOrder === "desc" ? ArrowDownWideNarrow : ArrowUpWideNarrow;
  const sortLabel = sortOrder === "desc" ? "新しい順" : "古い順";
  const nextSortOrder = sortOrder === "desc" ? "asc" : "desc";
  const nextSortLabel = sortOrder === "desc" ? "古い順に並べ替え" : "新しい順に並べ替え";
  return (
    <main className="single-column">
      <section className="panel">
        <div className="panel-heading">
          <h3>最新ログ</h3>
          <div className="log-toolbar">
            <div className="segmented" role="tablist" aria-label="ログの絞り込み">
              {filters.map((filter) => (
                <button
                  key={filter}
                  type="button"
                  className={activeFilter === filter ? "active" : ""}
                  onClick={() => onFilterChange(filter)}
                >
                  {LOG_FILTER_LABELS[filter]}
                </button>
              ))}
            </div>
            <button
              className="sort-button"
              type="button"
              onClick={() => onSortOrderChange(nextSortOrder)}
              title={nextSortLabel}
              aria-label={nextSortLabel}
            >
              <SortIcon size={16} />
              <span>{sortLabel}</span>
            </button>
          </div>
        </div>
        {logs.length ? (
          <div className="log-view" role="log">
            {logs.map((entry) => (
              <div className={`log-line log-${entry.level}`} key={entry.id}>
                <time>{entry.timestamp ?? "--:--:--"}</time>
                <span>{LOG_FILTER_LABELS[entry.level]}</span>
                <p>{entry.message}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="muted">条件に合うログはありません。</p>
        )}
      </section>
    </main>
  );
}

function BackupsView({ snapshot }: { snapshot: MinecraftSnapshot }) {
  return (
    <main className="single-column">
      <section className="panel">
        <div className="panel-heading">
          <h3>バックアップ</h3>
          <span className="subtle">{snapshot.backups.length}件</span>
        </div>
        {snapshot.backups.length ? (
          <div className="backup-grid">
            {snapshot.backups.map((backup) => (
              <article className="backup-item" key={backup.path}>
                <DatabaseBackup size={20} />
                <div>
                  <h4>{backup.name}</h4>
                  <p>{backup.createdAt ? new Date(backup.createdAt).toLocaleString("ja-JP") : "日付不明"}</p>
                </div>
                <span>{backup.sizeHuman}</span>
              </article>
            ))}
          </div>
        ) : (
          <p className="muted">バックアップは見つかりません。</p>
        )}
      </section>
    </main>
  );
}

function SettingsView({ snapshot, mode }: { snapshot: MinecraftSnapshot; mode: "local" | "cloud" }) {
  return (
    <main className="content-grid">
      <section className="panel">
        <h3>セキュリティ</h3>
        <dl className="facts">
          <Fact icon={ShieldCheck} label="モード" value={mode === "local" ? "127.0.0.1 ローカル待受" : "Cloudflare Access保護"} />
          <Fact label="RCON" value="無効" />
          <Fact label="ゲームポート" value="Tailscaleのみ" />
          <Fact label="操作" value="許可リストのみ" />
        </dl>
      </section>
      <section className="panel">
        <h3>パス</h3>
        <dl className="facts">
          <Fact label="エージェント" value={snapshot.agent.version} />
          <Fact label="launchdラベル" value={snapshot.launchd.label} />
          <Fact label="サーバーディレクトリ" value={snapshot.disk.serverDir ?? UNKNOWN_TEXT} />
          <Fact label="バックアップディレクトリ" value={snapshot.disk.backupsDir ?? UNKNOWN_TEXT} />
          <Fact label="プロセス" value={snapshot.process.command ?? UNKNOWN_TEXT} />
          <Fact label="バックアップ容量" value={snapshot.disk.backupsDirHuman ?? UNKNOWN_TEXT} />
        </dl>
      </section>
    </main>
  );
}

function LoadingView() {
  return (
    <main className="empty-state">
      <Loader2 className="spin" size={30} />
      <h2>ダッシュボードを読み込み中</h2>
    </main>
  );
}

function EmptyView() {
  return (
    <main className="empty-state">
      <Server size={34} />
      <h2>まだ状態データがありません</h2>
      <p>Mac miniエージェントからの状態データを待っています。</p>
    </main>
  );
}

function ConfirmModal({
  action,
  busy,
  onCancel,
  onConfirm,
}: {
  action: CommandType;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const meta = ACTION_META[action];
  const Icon = meta.icon;
  return (
    <div className="modal-backdrop" role="presentation">
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="confirm-title">
        <div className="modal-icon">
          <Icon size={24} />
        </div>
        <h3 id="confirm-title">サーバーを{meta.label}しますか？</h3>
        <p>Minecraftサービスの稼働状態を変更します。</p>
        <div className="modal-actions">
          <button type="button" className="action-button secondary" onClick={onCancel} disabled={busy}>
            キャンセル
          </button>
          <button type="button" className="action-button danger" onClick={onConfirm} disabled={busy}>
            {busy ? <Loader2 className="spin" size={18} /> : <Icon size={18} />}
            <span>{meta.label}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

function ToastStack({ toasts, onDismiss }: { toasts: Toast[]; onDismiss: (id: string) => void }) {
  return (
    <div className="toast-stack" role="status" aria-live="polite">
      {toasts.map((toast) => (
        <button key={toast.id} className={`toast toast-${toast.tone}`} type="button" onClick={() => onDismiss(toast.id)}>
          <span>{toast.message}</span>
          <X size={16} />
        </button>
      ))}
    </div>
  );
}

function TabButton({
  active,
  icon: Icon,
  label,
  onClick,
}: {
  active: boolean;
  icon: typeof Gauge;
  label: string;
  onClick: () => void;
}) {
  return (
    <button type="button" className={active ? "active" : ""} onClick={onClick}>
      <Icon size={17} />
      <span>{label}</span>
    </button>
  );
}

function MiniMetric({ icon: Icon, label, value }: { icon: typeof Users; label: string; value: string }) {
  return (
    <div className="mini-metric">
      <Icon size={17} />
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function Metric({
  icon: Icon,
  label,
  value,
  detail,
}: {
  icon: typeof Server;
  label: string;
  value: string;
  detail: string;
}) {
  return (
    <div className="metric">
      <Icon size={22} />
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{detail}</small>
    </div>
  );
}

function Fact({ label, value, icon: Icon }: { label: string; value: string; icon?: typeof ShieldCheck }) {
  return (
    <div className="fact">
      <dt>{Icon ? <Icon size={16} /> : null}{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function playersLabel(snapshot: MinecraftSnapshot): string {
  const online = snapshot.ping.onlinePlayers;
  const max = snapshot.ping.maxPlayers;
  if (online == null || max == null) return UNKNOWN_TEXT;
  return `${online}/${max}`;
}

function formatRelative(iso: string): string {
  const seconds = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 1000));
  if (seconds < 5) return "たった今";
  if (seconds < 60) return `${seconds}秒前`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}分前`;
  const hours = Math.floor(minutes / 60);
  return `${hours}時間前`;
}

function duration(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(whole / 3600);
  const minutes = Math.floor((whole % 3600) / 60);
  if (hours) return `${hours}時間 ${minutes}分`;
  return `${minutes}分`;
}

function durationMs(milliseconds: number): string {
  const seconds = Math.max(1, Math.round(milliseconds / 1000));
  if (seconds < 60) return `${seconds}秒`;
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return remainder ? `${minutes}分 ${remainder}秒` : `${minutes}分`;
}

function storageDetail(snapshot: MinecraftSnapshot): string {
  const free = snapshot.disk.freeHuman ?? UNKNOWN_TEXT;
  const location = snapshot.disk.external ? "外付け" : "内蔵";
  return `空き ${free} / ${location}`;
}

function storageWarningMessage(snapshot: MinecraftSnapshot): string | null {
  if (snapshot.disk.error) return `ストレージ確認で注意が必要です: ${localizedErrorMessage(snapshot.disk.error)}`;
  if (snapshot.disk.writable === false) return "Minecraftサーバーディレクトリに書き込めません。";
  if (snapshot.disk.freeBytes != null && snapshot.disk.freeBytes < 10 * 1024 * 1024 * 1024) {
    return `ストレージ残量が少なくなっています: 空き ${snapshot.disk.freeHuman ?? UNKNOWN_TEXT}`;
  }
  return null;
}

function bytesLabel(bytes: number | null | undefined): string {
  const value = humanBytes(bytes);
  return value === "unknown" ? UNKNOWN_TEXT : value;
}

function statusReasonLabel(reason: string): string {
  return STATUS_REASON_LABELS[reason] ?? reason;
}

function launchdStateLabel(rawState: string | null, running: boolean): string {
  if (!rawState) return running ? "稼働中" : "停止中";
  return (
    {
      running: "稼働中",
      exited: "終了",
      waiting: "待機中",
      spawning: "起動中",
    }[rawState] ?? rawState
  );
}

function localizedErrorMessage(message: string): string {
  return API_ERROR_LABELS[message] ?? message;
}

function preferredTheme(): Theme {
  const saved = localStorage.getItem("minecraft-dashboard-theme");
  if (saved === "light" || saved === "dark") return saved;
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}
